import { ValidationMessage } from "@/flokit/core/validation-message/validation-message";
import { useHtmlId } from "@/flokit/utils/html/use-html-id";
import { cx } from "class-variance-authority";
import React from "react";
import { useIsFormDisabled } from "../contexts/disabled-form-context";
import { InputDescription } from "../input-label/input-description";
import { InputLabel } from "../input-label/input-label";
import {
  getCorrectHtmlAttributesForInput,
  normalizeLabelProp,
} from "../input-label/label-prop";
import { invalidInputOutlineClasses } from "../shared-styles";
import {
  InputValidationProps,
  LabelledInputProps,
  SelectionRange,
} from "../types";
import pluralize from "pluralize";
import { cn } from "@/flokit/utils/styling/cn";

export type TextInputProps = LabelledInputProps &
  InputValidationProps & {
    // QA Tests depend on this
    name?: string;
    value: string;
    readOnly?: boolean;
    onChange: (
      newValue: string,
      event?: React.ChangeEvent<HTMLInputElement>,
    ) => void;

    isDisabled?: boolean;

    type?:
      | "datetime-local"
      | "text"
      | "date"
      | "time"
      | "number"
      | "email"
      | "password"
      | "tel";
    min?: number;
    max?: number;
    placeholder?: string;

    onSelect?: (newSelectionRange: SelectionRange) => void;
    onWheel?: (e: React.WheelEvent<HTMLInputElement>) => void;

    outlineColor?: "navy" | "magenta";
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    UNSAFE_ClassName?: string;
    /**
     * The `id` attribute of a `<datalist>` element. The `<option>`s in the `<datalist>` will be displayed as suggestions when the user types in the `<input />`.
     */
    datalistId?: string;
    characterLimit?: number;
  };
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const isFormDisabled = useIsFormDisabled();
    const generatedId = useHtmlId();
    const descriptionId = useHtmlId();
    const labelMethod = normalizeLabelProp(props.label);
    const disabled = props.isDisabled ?? isFormDisabled;

    const characterLimitId = useHtmlId();
    const isAboveCharacterLimit =
      (props.value?.length ?? 0) > (props.characterLimit ?? Infinity);

    return (
      <div className={cx("flex flex-col", "w-full", props.UNSAFE_ClassName)}>
        {labelMethod.type === "default" || labelMethod.type === "hidden" ? (
          <div className="flex flex-row items-center justify-between">
            <InputLabel
              labelMethod={labelMethod}
              interactivity="interactive"
              isRequired={props.isRequired}
              isDisabled={disabled}
              tooltip={props.tooltip}
              idOfElementToBeLabeled={generatedId}
            />
            {props.characterLimit ? (
              <p
                className={cn(
                  "self-end font-inter text-sm font-medium",
                  isAboveCharacterLimit ? "text-ui-red" : "text-medium-hippo",
                )}
                id={characterLimitId}
              >{`(${props.value?.length ?? 0}/${props.characterLimit}) ${pluralize("characters", props.characterLimit, false)}`}</p>
            ) : null}
          </div>
        ) : null}
        {props.description ? (
          <div className="pt-0.5">
            <InputDescription id={descriptionId}>
              {props.description}
            </InputDescription>
          </div>
        ) : null}
        <input
          readOnly={props.readOnly}
          ref={ref}
          value={props.value}
          onChange={(e) => {
            props.onChange(e.target.value, e);
          }}
          placeholder={props.placeholder}
          type={props.type}
          {...getCorrectHtmlAttributesForInput({
            labelProp: props.label,
            htmlId: generatedId,
            elementIsNonInteractive: false,
            fieldIsRequired: props.isRequired,
          })}
          aria-describedby={
            props.description
              ? `${descriptionId} ${characterLimitId}`
              : labelMethod.type === "aria-describedby"
                ? labelMethod.idOfDescribingElement
                : undefined
          }
          disabled={disabled || props.readOnly}
          onFocus={(e) => {
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            props.onBlur?.(e);
          }}
          onSelect={(e) => {
            props?.onSelect?.({
              start: (e.target as HTMLInputElement).selectionStart,
              end: (e.target as HTMLInputElement).selectionEnd,
            });
          }}
          onWheel={props.onWheel}
          onKeyDown={props.onKeyDown}
          min={props.min}
          max={props.max}
          className={cx(
            labelMethod.type !== "hidden" ? "mt-2" : "",
            "px-4 py-2",
            "rounded border border-dublin-gray",
            "focus:outline-none focus:ring-1",
            props.outlineColor === "navy"
              ? "focus:border-navy focus:ring-navy"
              : "focus:border-flo-magenta focus:ring-flo-magenta",
            "text-base",
            "disabled:text-light-elephant",
            props.shouldShowValidation && invalidInputOutlineClasses,
          )}
          name={props.name}
          list={props.datalistId}
        />
        {props.shouldShowValidation ? (
          <div className="block pt-2">
            <ValidationMessage
              shouldShowValidation={props.shouldShowValidation}
              validationMessage={props.validationMessage}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

// https://florecruit.atlassian.net/browse/FWD-403
// https://stackoverflow.com/a/69497807
export function inputOnWheelPreventChange(
  e: React.WheelEvent<HTMLInputElement>,
) {
  if (!e.target) {
    return;
  }

  // Blur and focus callbacks only available on HTMLElement
  const target = e.target;
  if (target instanceof HTMLElement) {
    // Prevent the input value change
    target.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current function is done)
    setTimeout(() => {
      target.focus();
    }, 0);
  }
}
